import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarDismiss, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';

import { ViToast, ViToastConfig } from './toast';
import { ViToastComponent } from './toast.component';

export interface SnackBarConfig {
  action?: string;
  duration?: number;
  x?: 'start' | 'center' | 'end' | 'left' | 'right';
  y?: 'top' | 'bottom';
}

@Injectable()
export class ViToastService {
  private readonly defaultConfig: ViToastConfig;
  constructor(private snackBar: MatSnackBar) {
    this.defaultConfig = {
      type: 'info',
      duration: 5000,
      x: 'center',
      y: 'bottom',
    };
  }

  open(toast: ViToast, config?: ViToastConfig): Observable<MatSnackBarDismiss> {
    if (config) {
      switch (config.type) {
        case 'info':
          return this.info(toast, config);
        case 'success':
          return this.success(toast, config);
        case 'error':
          return this.error(toast, config);
        case 'warning':
          return this.warning(toast, config);
        default:
          return this.info(toast, config);
      }
    }

    return this.info(toast);
  }

  info(toast: ViToast, config?: ViToastConfig): Observable<MatSnackBarDismiss> {
    return this.infoRef(toast, config).afterDismissed();
  }

  warning(toast: ViToast, config?: ViToastConfig): Observable<MatSnackBarDismiss> {
    return this.warningRef(toast, config).afterDismissed();
  }

  success(toast: ViToast, config?: ViToastConfig): Observable<MatSnackBarDismiss> {
    return this.successRef(toast, config).afterDismissed();
  }

  error(toast: ViToast, config?: ViToastConfig): Observable<MatSnackBarDismiss> {
    return this.errorRef(toast, config).afterDismissed();
  }

  openRef(toast: ViToast, config?: ViToastConfig): MatSnackBarRef<ViToastComponent> {
    if (config) {
      switch (config.type) {
        case 'info':
          return this.infoRef(toast, config);
        case 'success':
          return this.successRef(toast, config);
        case 'error':
          return this.errorRef(toast, config);
        case 'warning':
          return this.warningRef(toast, config);
        default:
          return this.infoRef(toast, config);
      }
    }

    return this.infoRef(toast);
  }

  infoRef(toast: ViToast, config?: ViToastConfig): MatSnackBarRef<ViToastComponent> {
    return this.openSnackBar(toast, { ...config, type: 'info' });
  }

  warningRef(toast: ViToast, config?: ViToastConfig): MatSnackBarRef<ViToastComponent> {
    return this.openSnackBar(toast, { ...config, type: 'warning' });
  }

  successRef(toast: ViToast, config?: ViToastConfig): MatSnackBarRef<ViToastComponent> {
    return this.openSnackBar(toast, { ...config, type: 'success' });
  }

  errorRef(toast: ViToast, config?: ViToastConfig): MatSnackBarRef<ViToastComponent> {
    return this.openSnackBar(toast, { ...config, type: 'error' });
  }

  private openSnackBar(toast: ViToast, config: ViToastConfig): MatSnackBarRef<ViToastComponent> {
    const _config = { ...this.defaultConfig, ...config };
    const configPanelClass = _config.panelClass ? _config.panelClass : [];

    return this.snackBar.openFromComponent(ViToastComponent, {
      duration: _config.duration,
      panelClass: [_config.type, ...configPanelClass],
      horizontalPosition: _config.x,
      verticalPosition: _config.y,
      data: { ...toast, type: _config.type },
    });
  }
}
